import { useEffect, useState } from "react";
import { useAuthContext } from "../../context/AuthContext";
import getDashboardCounts from "../../apis/getDashboardCounts";
import ComponentLoader from "../Loader/ComponentLoader";
import moment from "moment";
import { Row, Col, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faCalendar,
  faInfo,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";
import { connectiqGetFirmUsage } from "../../apis/connectiqAPI";

const fmt = (n) => {
  if (!n) return "0";
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return Number(n).toLocaleString();
};

const fmtCost = (n) => `$${(n || 0).toFixed(2)}`;

const CALL_LABELS = {
  search_call1: "Search — pass 1",
  search_call2: "Search — pass 2",
  profile_chat: "Profile chat",
};

const CALL_PILL = {
  search_call1: { background: "#f0eefe", color: "#534AB7" },
  search_call2: { background: "#171717", color: "#fff" },
  profile_chat: { background: "#eef9ee", color: "#2f7a3c" },
};

const RANGES = [
  { label: "7d", value: "7" },
  { label: "30d", value: "30" },
  { label: "90d", value: "90" },
  { label: "All", value: "all" },
];

// ── Usage Modal ───────────────────────────────────────────────────────────────

const UsageModal = ({ show, onHide, userSlug }) => {
  const [range, setRange] = useState("30");
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (show && userSlug) load();
  }, [show, userSlug, range]);

  const load = () => {
    setIsLoading(true);
    connectiqGetFirmUsage(userSlug, range)
      .then((res) => {
        if (res?.data?.status === 1) setData(res.data);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header
        style={{
          borderBottom: "0.5px solid #ececec",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "26px",
              height: "26px",
              borderRadius: "6px",
              background: "#171717",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "9px",
              fontWeight: "500",
            }}
          >
            IQ
          </div>
          <div>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "500",
                color: "#171717",
                lineHeight: 1.2,
              }}
            >
              ConnectIQ usage
            </div>
            <div
              style={{ fontSize: "12px", color: "#8c8c8c", fontWeight: "400" }}
            >
              Token usage and estimated cost
            </div>
          </div>
        </div>
        <button
          onClick={onHide}
          style={{
            background: "none",
            border: "0.5px solid #dee2e6",
            borderRadius: "6px",
            width: "28px",
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#8c8c8c",
            fontSize: "14px",
            flexShrink: 0,
          }}
        >
          &#x2715;
        </button>
      </Modal.Header>

      <Modal.Body style={{ padding: "20px" }}>
        {/* Range pills */}
        <div style={{ display: "flex", gap: "5px", marginBottom: "20px" }}>
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              style={{
                padding: "4px 12px",
                fontSize: "11.5px",
                borderRadius: "100px",
                border: "0.5px solid #dee2e6",
                background: range === r.value ? "#171717" : "#fff",
                color: range === r.value ? "#fff" : "#555",
                cursor: "pointer",
                fontFamily: "inherit",
                fontWeight: range === r.value ? "500" : "400",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div style={{ padding: "20px 0" }}>
            <ComponentLoader />
          </div>
        ) : !data?.totals || data.totals.call_count === 0 ? (
          <p style={{ color: "#8c8c8c", fontSize: "13px" }}>
            No usage recorded in this period.
          </p>
        ) : (
          <>
            {/* Stat cards */}
            <Row className="g-2 mb-4">
              {[
                {
                  label: "Total calls",
                  value: fmt(data.totals.call_count),
                  green: false,
                },
                {
                  label: "Total tokens",
                  value: fmt(data.totals.total_tokens),
                  green: false,
                },
                {
                  label: "Input tokens",
                  value: fmt(data.totals.total_input),
                  green: false,
                },
              ].map((s, i) => (
                <Col xs={6} md={4} key={i}>
                  <div
                    style={{
                      background: "#f7f7f7",
                      borderRadius: "8px",
                      padding: "14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#8c8c8c",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        marginBottom: "5px",
                      }}
                    >
                      {s.label}
                    </div>
                    <div
                      style={{
                        fontSize: "22px",
                        fontWeight: "500",
                        color: "#171717",
                        lineHeight: 1,
                      }}
                    >
                      {s.value}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            <div
              style={{ borderTop: "0.5px solid #ececec", marginBottom: "16px" }}
            />

            {/* Breakdown */}
            {data.breakdown?.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: "#8c8c8c",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: "10px",
                  }}
                >
                  Breakdown by type
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  {data.breakdown.map((row, i) => {
                    const pillStyle = CALL_PILL[row.call_type] || {
                      background: "#f7f7f7",
                      color: "#171717",
                    };
                    const pct =
                      data.totals.total_tokens > 0
                        ? Math.round(
                            (row.total_tokens / data.totals.total_tokens) * 100,
                          )
                        : 0;
                    return (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "10px 12px",
                          background: "#fafafa",
                          borderRadius: "8px",
                          border: "0.5px solid #ececec",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "3px 10px",
                            borderRadius: "100px",
                            fontWeight: "500",
                            flexShrink: 0,
                            minWidth: "112px",
                            textAlign: "center",
                            ...pillStyle,
                          }}
                        >
                          {CALL_LABELS[row.call_type] || row.call_type}
                        </span>
                        <div
                          style={{
                            flex: 1,
                            height: "4px",
                            background: "#e8e8e8",
                            borderRadius: "2px",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              width: `${pct}%`,
                              background: "#6C63FF",
                              borderRadius: "2px",
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "18px",
                            flexShrink: 0,
                          }}
                        >
                          {[
                            { label: "Calls", value: fmt(row.call_count) },
                            { label: "Tokens", value: fmt(row.total_tokens) },
                          ].map((m, mi) => (
                            <div key={mi} style={{ textAlign: "right" }}>
                              <div
                                style={{ fontSize: "10px", color: "#8c8c8c" }}
                              >
                                {m.label}
                              </div>
                              <div
                                style={{
                                  fontSize: "12.5px",
                                  fontWeight: "500",
                                  color: "#171717",
                                }}
                              >
                                {m.value}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const DashboardCounts = () => {
  const { userDetails } = useAuthContext();
  const parsed = JSON.parse(userDetails);
  const user_slug = parsed.user_slug;
  const user_primary_role = parsed.user_primary_role;

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (user_slug) {
      getCounts();
      if (user_primary_role !== "3") getUsageSummary();
    }
  }, [user_slug]);

  const getCounts = () => {
    getDashboardCounts(user_slug)
      .then((res) => {
        if (res?.data?.result) setData(res.data.result);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  const getUsageSummary = () => {
    connectiqGetFirmUsage(user_slug, "30")
      .then((res) => {
        if (res?.data?.status === 1) setUsageData(res.data);
      })
      .catch(() => {});
  };

  const displayContent = () => {
    if (!data) return null;
    return (
      <Row className="g-3">
        <Col md={3} key={1}>
          <div className="card shadow-lg">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="mb-2">Total Resources</h2>
                  <span className="text-dark text-lg fw-semibold">
                    {data?.resource_count}
                  </span>
                </div>
                <div
                  style={{
                    backgroundColor: "#FEEBC8",
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  <FontAwesomeIcon icon={faUser} size="2x" />
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col md={3} key={2}>
          <div className="card shadow-lg">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="mb-2">Available Parsing Credits</h2>
                  <span className="text-success text-lg fw-semibold">
                    {data?.firm_details?.parsing_credits}
                  </span>
                </div>
                <div
                  style={{
                    backgroundColor: "oklch(.905 .093 164.15)",
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  <FontAwesomeIcon icon={faInfo} size="2x" />
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col md={3} key={3}>
          <div className="card shadow-lg">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="mb-2">Renewal Date</h2>
                  <span className="text-warning text-lg fw-semibold">
                    {moment(data?.firm_details?.expiry_date).format(
                      "MMM Do, YYYY",
                    )}
                  </span>
                </div>
                <div
                  style={{
                    backgroundColor: "oklch(.901 .058 230.902)",
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  <FontAwesomeIcon icon={faCalendar} size="2x" />
                </div>
              </div>
            </div>
          </div>
        </Col>

        {/* ConnectIQ usage card — RM and firm owner only */}
        {user_primary_role !== "3" && (
          <Col md={3} key={4}>
            <div
              className="card shadow-lg"
              onClick={() => setShowModal(true)}
              style={{ cursor: "pointer", transition: "box-shadow 0.15s" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.boxShadow =
                  "0 4px 16px rgba(0,0,0,0.12)")
              }
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "")}
            >
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="mb-2">ConnectIQ Usage</h2>
                    <span
                      className="fw-semibold"
                      style={{ color: "#534AB7", fontSize: "1.1rem" }}
                    >
                      {usageData?.totals?.call_count
                        ? `${fmt(usageData.totals.total_tokens)} tokens`
                        : "No usage yet"}
                    </span>
                  </div>
                  <div
                    style={{
                      backgroundColor: "#f0eefe",
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      justifyContent: "center",
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faBolt}
                      size="2x"
                      style={{ color: "#534AB7" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Col>
        )}
      </Row>
    );
  };

  return (
    <>
      {isLoading ? <ComponentLoader /> : displayContent()}
      <UsageModal
        show={showModal}
        onHide={() => setShowModal(false)}
        userSlug={user_slug}
      />
    </>
  );
};

export default DashboardCounts;
